
import React, { useContext, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { CoinsButton, Tooltip } from '@dataplace.ai/ui-components/atoms'
import { AuthContext } from '@dataplace.ai/features'
import { ISectionTile } from '../../../slice/@types/ISectionTile'
import { Tile } from '../Tile'
import { HintsGlider } from '../HintsGlider'
import { RootState } from '../../../../../redux/store'
import { ITile } from '../../../slice/@types/ITile'
import { tilesData } from '../../../utils/tilesData'
import { addTileAction } from '../../../slice/analysisSlice'
import { AddAnotherRange } from '../../atoms'
import { ReactComponent as Help } from '../../../../../../../../libs/shared/assets/src/lib/icons/dataplace/help-circle.svg'

const { v4: uuidv4 } = require('uuid')

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 2rem;
`

const Header = styled.div(({ theme }) => {
  const {
    typography, palette,
  } = theme
  return css`
    align-items: center;
    display: flex;
    color: ${palette.black};
    width: 100%;
    padding: 1rem 2.5rem;
    box-sizing: border-box;
    border-bottom: 1px solid ${palette.light.darker};
    border-top: 1px solid ${palette.light.darker};
    font-size: ${typography.big.pt_18_semibold.fontSize};
    font-weight: ${typography.big.pt_18_semibold.fontWeight};
    line-height: ${typography.big.pt_18_semibold.lineHeight};

    > svg {
      margin-right: 0.5rem;
    }
  `
})

const TilesWrapper = styled.div(
  () => css`
    width: 100%;
    display: flex;
    flex-direction: column;
    padding: 2rem;
    box-sizing: border-box;

    @media (max-width: 1200px){
      padding: 1rem;
    }
  `,
)

const TileWrapper = styled.div(
  () => css`
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    flex-wrap: nowrap;
    margin-bottom: 2rem;
  `,
)

const StyledTileWrapper = styled(TileWrapper)`
  margin-bottom:5rem;
  `
const AddAnotherLeftWrapper = styled.div`
      margin-top:0;
      transition: 0.4s border-color;
      :hover{
        border-color: #234596;
      }
      svg{
        margin-right:0.5rem;
      }
  `

const HintsAndAskWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 22%;
  align-items: flex-end;
`

const TriggerWrapper = styled.a(({ theme }) => {
  const { palette } = theme
  return css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    background-color: ${palette.blue};
    border-radius: 50%;
    cursor: pointer;

    > svg {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background-color: ${palette.light.white};
      ;
      > path {
        fill: ${palette.blue};
      }
    }
  `
})

interface ISectionTileProps {
  sectionTile: ISectionTile
  userId: string
  isPdf?: boolean
  staticImage?: string
  locationPDF?: {
    address: string,
    lat: number,
    lng: number,
  }
}

const tilesWithoutAddAnother = ['land_use_plan', 'look_alike_big_ben', 'scoring_model_big_ben', 'satellite', 'cities_population', 'municipality_population']

export const SectionTile: React.FC<ISectionTileProps> = ({
  sectionTile, userId, isPdf, staticImage, locationPDF,
}) => {
  // constants
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const {
    label, tiles, icon,
  } = sectionTile

  const {
    showHints, canBeSave,
  } = useSelector((state: RootState) => state.analysis)
  const { currentSubscriptionData } = useSelector((state: RootState) => state.location)
  const authContext = useContext(AuthContext)

  // we need ref to get current state in event listener
  const [token, _setToken] = useState('')
  const tokenRef = React.useRef(token)
  const setToken = (newTokenRef:string) => {
    tokenRef.current = newTokenRef
    _setToken(newTokenRef)
  }

  // functions
  const fetchTileScheme = (categoryId:string, tileId: string) => {
    // eslint-disable-next-line prefer-destructuring
    const schemeTileId = tileId.split('-')[0]
    return tilesData.find(cat =>
      cat.id === categoryId)?.tiles.find(tile => tile.id === schemeTileId) as ITile
  }

  const handleSortTypeGroups = (tiles: ITile[]) => {
    const tilesTypes: string[] = []
    tiles.forEach(tile => !tilesTypes.includes(tile.id.split('-')[0]) && tilesTypes.push(tile.id.split('-')[0]))

    return tilesTypes.map((tilesType) => {
      const componentsArray = (tiles.filter(tile => tile.id.split('-')[0] === tilesType).map(item =>
        (
          <TileWrapper key={item.id}>
            <Tile
              key={item.id}
              isPdf={isPdf}
              locationPDF={locationPDF}
              sectionTile={sectionTile}
              staticImage={staticImage}
              tile={item}
              userId={userId}
            />
            <HintsAndAskWrapper>
              {!!item?.hints?.length && showHints && !isPdf && <HintsGlider hints={item.hints} />}
              {!isPdf
              && (
                <Tooltip
                  content={t('placeme.ask_circle_menu.knowledge_base')}
                  position='left top'
                >
                  <TriggerWrapper
                    href='https://knowledge.dataplace.ai/knowledge'
                    target='_blank'
                  >
                    <Help viewBox='1 1 14 14' />
                  </TriggerWrapper>
                </Tooltip>
              )}
            </HintsAndAskWrapper>
          </TileWrapper>
        ))
      )
      if (tiles.filter(tile =>
        (tile.id.split('-')[0] === tilesType && !tilesWithoutAddAnother.includes(tile.id.split('-')[0]))).slice(-1)[0]?.data?.value && canBeSave && !isPdf) {
        componentsArray.push(
          <StyledTileWrapper key={uuidv4()}>
            <AddAnotherRange
              onClick={() => {
                dispatch(addTileAction(token, sectionTile, fetchTileScheme(sectionTile.id, tilesType)))
              }}
              style={{
                width: '74%',
                padding: '8px',
                backgroundColor: '#FAFBFC',
              }}
            >
              <AddAnotherLeftWrapper>
                <span>{`${['transport', 'cannibalization'].includes(tilesType) ? t('placeme.tiles.add_another_localization') : t('placeme.tiles.add_another_range')}: ${t(`${tiles.filter(tile => tile.id.split('-')[0] === tilesType)[0].label}`)}`}</span>
                <span>{['transport', 'cannibalization'].includes(tilesType) ? t('placeme.tiles.add_another_localization_description') : t('placeme.tiles.add_another_range_description')}</span>
              </AddAnotherLeftWrapper>
              {/* <ButtonWrapper> */}
              <CoinsButton
                isUnlimited={currentSubscriptionData?.value?.planExact?.includes('unlimited') || currentSubscriptionData?.value?.plan === 'white'}
                onClick={() => {}}
                subscription
                text={t('placeme.add_another_tile')}
                tileId={tiles.filter(tile => tile.id.split('-')[0] === tilesType)[0].id ?? ''}
                value={1}
              />
            </AddAnotherRange>
          </StyledTileWrapper>,
        )
      }
      return componentsArray
    })
  }

  // hooks
  useEffect(() => {
    // scroll into view for last added tile in section
    const { id } = tiles[tiles.length - 1]
    const sectionElem = document.querySelector(`#${id}`)
    if (sectionElem && !sectionTile.tiles.find(item => item.id === id)?.data?.mapLocation) {
      sectionElem.scrollIntoView()
    }
  }, [sectionTile])

  useEffect(() => {
    authContext.userData?.user?.getIdToken(true)?.then((response) => {
      setToken(response)
    })
  }, [authContext])

  return (
    <Wrapper>
      <Header>
        {!isPdf && icon}
        <span>{t(`${label}`)}</span>
      </Header>
      <TilesWrapper>
        { handleSortTypeGroups(tiles)}
      </TilesWrapper>
    </Wrapper>
  )
}
